import pb from './pb';
import type { Product } from '../types/product';
import type { ProductSample } from '../types/productSample';
import type { ProductDownload } from '../types/productDownload';
import type { ProductImage } from '../types/productImage';
import type { ProductVersion } from '../types/productVersion';

export async function fetchProducts(): Promise<Product[]> {
  // PocketBase collection rules handle visibility:
  // - Non-admin / unauthenticated: only published products
  // - Admin (is_admin = true): all products including unpublished
  return pb.collection('products').getFullList<Product>({
    sort: 'sort_order,name',
  });
}

export async function fetchProductBySlug(slug: string): Promise<Product> {
  return pb.collection('products').getFirstListItem<Product>(`slug = "${slug}"`);
}

export function getProductImageUrl(product: Product): string {
  if (!product.image) return '';
  return `${pb.baseURL}/api/files/products/${product.id}/${product.image}`;
}

export async function fetchProductSamples(productId: string): Promise<ProductSample[]> {
  return pb.collection('product_samples').getFullList<ProductSample>({
    filter: `product = "${productId}"`,
    sort: 'sort_order',
  });
}

export function getSampleAudioUrl(sample: ProductSample): string {
  if (!sample.audio) return '';
  return `${pb.baseURL}/api/files/product_samples/${sample.id}/${sample.audio}`;
}

export async function fetchProductVersions(productId: string): Promise<ProductVersion[]> {
  const all = await pb.collection('product_versions').getFullList<ProductVersion>({
    filter: `product = "${productId}"`,
  });

  const sorted = [...all].sort((a, b) => compareSemver(b.version, a.version));
  return sorted;
}

export async function fetchLatestProductVersion(productId: string): Promise<ProductVersion | null> {
  const versions = await fetchProductVersions(productId);
  return versions.length > 0 ? versions[0] : null;
}

export async function fetchProductDownloads(productVersionId: string): Promise<ProductDownload[]> {
  return pb.collection('product_downloads').getFullList<ProductDownload>({
    filter: `product_version = "${productVersionId}"`,
  });
}

function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pbv = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pbv.length); i++) {
    const na = pa[i] ?? 0;
    const nb = pbv[i] ?? 0;
    if (na !== nb) return na - nb;
  }
  return 0;
}

export function getDownloadFileUrl(download: ProductDownload): string {
  if (!download.file) return '';
  return `${pb.baseURL}/api/files/product_downloads/${download.id}/${download.file}`;
}

export async function fetchProductImages(productId: string): Promise<ProductImage[]> {
  return pb.collection('product_images').getFullList<ProductImage>({
    filter: `product = "${productId}"`,
    sort: 'sort_order',
  });
}

export function getGalleryImageUrl(image: ProductImage, thumbSize?: string): string {
  if (!image.image) return '';
  const base = `${pb.baseURL}/api/files/product_images/${image.id}/${image.image}`;
  if (thumbSize) return `${base}?thumb=${thumbSize}`;
  return base;
}
